import * as ol from "ol";
import * as React from "react";
import { useAnimationFrame } from "./useAnimationFrame";

export const MOUSE_DRAG_THRESHOLD = 5;
type Coordinate = [number, number];

/**
 * Applied to an openlayers map element, produces drag start, drag animation, drag end, and click events
 */
type MouseState =
	| { type: "none" }
	| { type: "down"; downCoord: Coordinate }
	| {
			type: "moved";
			downCoord: Coordinate;
			drift: number;
			lastScreenX: number;
			lastScreenY: number;
	  }
	| { type: "dragging" }
	| { type: "click"; coord: Coordinate }
	| { type: "dragEnd" };

export const useOpenLayersDrag = (options: {
	target: React.MutableRefObject<ol.Map | null>;
	onDragStart: (projX: number, projY: number) => void;
	onDrag: (projX: number, projY: number, pixelX: number, pixelY: number) => void;
	onDragEnd: () => void;
	onClick?: (projX: number, projY: number) => void;
}) => {
	const [mouse, setMouse] = React.useState<MouseState>({ type: "none" });
	const capturedPointer = React.useRef<number | undefined>(undefined);

	const activeTransitionTarget = React.useRef<MouseState["type"]>();

	const transitionState = React.useCallback(
		(targetState: MouseState) => {
			activeTransitionTarget.current = targetState.type;

			setMouse(mouseNow => {
				if (mouseNow === mouse) {
					return targetState;
				} else {
					return mouseNow;
				}
			});
		},
		[mouse, setMouse]
	);

	const animate = useAnimationFrame();

	const { onDragStart, onDragEnd, onDrag, onClick, target } = options;

	React.useEffect(() => {
		if (activeTransitionTarget.current === mouse.type) {
			activeTransitionTarget.current = undefined;
		}

		// capture target.current else it may change before the callback functions run
		const current = target.current;

		switch (mouse.type) {
			case "none": {
				const pointerDownListener = (e: ol.MapBrowserPointerEvent) => {
					const browserEvent = e.originalEvent as PointerEvent;

					if (browserEvent.button !== 0) {
						return;
					}

					setMouse({
						type: "down",
						downCoord: e.coordinate as Coordinate
					});
				};

				const viewportElement = current!.getViewport();

				if (
					capturedPointer.current !== undefined &&
					(viewportElement.hasPointerCapture === undefined ||
						viewportElement.hasPointerCapture(capturedPointer.current))
				) {
					viewportElement.releasePointerCapture(capturedPointer.current);
					capturedPointer.current = undefined;
				}

				current!.on("pointerdown", pointerDownListener as any);
				return () => {
					current!.un("pointerdown", pointerDownListener as any);
				};
			}

			case "down": {
				const pointerMoveListener = (e: ol.MapBrowserPointerEvent) => {
					const browserEvent = e.originalEvent as PointerEvent;
					current!.getViewport().setPointerCapture(browserEvent.pointerId);
					capturedPointer.current = browserEvent.pointerId;

					transitionState({
						type: "moved",
						downCoord: mouse.downCoord,
						drift: 0,
						lastScreenX: browserEvent.screenX,
						lastScreenY: browserEvent.screenY
					});
				};

				current!.on("pointermove", pointerMoveListener as any);

				document.body.onpointerup = () => setMouse({ type: "click", coord: mouse.downCoord });
				document.body.onpointerdown = () => transitionState({ type: "none" });

				return () => {
					current!.un("pointermove", pointerMoveListener as any);
					document.body.onpointerup = null;
					document.body.onpointerdown = null;
				};
			}

			case "moved": {
				const pointerMoveListener = (e: ol.MapBrowserPointerEvent) => {
					const browserEvent = e.originalEvent as PointerEvent;

					if (activeTransitionTarget.current) {
						return;
					}

					if (mouse.drift > MOUSE_DRAG_THRESHOLD) {
						if (onDragStart) {
							onDragStart(...mouse.downCoord);
						}

						transitionState({
							type: "dragging"
						});
					} else {
						transitionState({
							type: "moved",
							downCoord: mouse.downCoord,
							drift:
								mouse.drift +
								Math.abs(browserEvent.screenX - mouse.lastScreenX) +
								Math.abs(browserEvent.screenY - mouse.lastScreenY),
							lastScreenX: browserEvent.screenX,
							lastScreenY: browserEvent.screenY
						});
					}
				};

				current!.on("pointermove", pointerMoveListener as any);

				document.body.onpointerup = () => {
					setMouse(mouseNow => {
						if (mouseNow.type === "moved") {
							return { type: "click", coord: mouse.downCoord };
						} else if (mouseNow.type === "dragging") {
							return { type: "dragEnd" };
						}
						return { type: "none" };
					});
				};
				document.body.onpointerdown = () => transitionState({ type: "none" });

				return () => {
					current!.un("pointermove", pointerMoveListener as any);

					document.body.onpointerup = null;
					document.body.onpointerdown = null;
				};
			}

			case "dragging": {
				const pointerMoveListener = (e: ol.MapBrowserPointerEvent) => {
					animate(() => onDrag(e.coordinate[0], e.coordinate[1], e.pixel[0], e.pixel[1]));
				};

				current!.on("pointermove", pointerMoveListener as any);
				document.body.onpointerup = () => setMouse({ type: "dragEnd" });
				document.body.onpointerdown = () => transitionState({ type: "none" });

				return () => {
					current!.un("pointermove", pointerMoveListener as any);
					document.body.onpointerup = null;
					document.body.onpointerdown = null;
				};
			}

			case "click":
				if (onClick) {
					onClick(...mouse.coord);
				}
				setMouse({ type: "none" });
				break;

			case "dragEnd":
				if (onDragEnd) {
					onDragEnd();
				}

				setMouse({ type: "none" });
				break;
		}
	}, [
		onDragStart,
		onDragEnd,
		onDrag,
		onClick,
		mouse,
		setMouse,
		capturedPointer,
		transitionState,
		animate,
		target
	]);
};
